import { useCallback, useEffect, useRef } from 'react';

const ACTIVITY_REFRESH_DELAY_MS = 5_000;
const ACTIVITY_FOLLOW_UP_DELAY_MS = 40_000;

type ContributorsActivityRefreshOptions = {
	contentId: string;
	enabled: boolean;
	refreshNow: (options?: { queueIfInFlight?: boolean }) => Promise<void>;
};

const refreshIfVisible = (
	refreshNow: ContributorsActivityRefreshOptions['refreshNow'],
	isHidden: boolean,
): void => {
	if (!isHidden) {
		void refreshNow({ queueIfInFlight: true });
	}
};

export const useContributorsActivityRefresh = ({
	contentId,
	enabled,
	refreshNow,
}: ContributorsActivityRefreshOptions): (() => void) => {
	const activityRef = useRef<(() => void) | undefined>(undefined);
	const onActivity = useCallback(() => activityRef.current?.(), []);

	useEffect(() => {
		if (!enabled) {
			return;
		}

		let activityTimer: ReturnType<typeof setTimeout> | undefined;
		let followUpTimer: ReturnType<typeof setTimeout> | undefined;
		let followUpDeadline: number | undefined;

		const clearTimers = () => {
			if (activityTimer !== undefined) {
				clearTimeout(activityTimer);
				activityTimer = undefined;
			}
			if (followUpTimer !== undefined) {
				clearTimeout(followUpTimer);
				followUpTimer = undefined;
			}
		};

		// Refresh the batch five seconds after its first activity, even if edits continue.
		const refreshAfterActivity = () => {
			activityTimer = undefined;
			refreshIfVisible(refreshNow, document.hidden);
		};
		// Recheck once forty seconds after the latest activity to catch delayed agent attribution.
		const refreshAfterActivitySettles = () => {
			followUpTimer = undefined;
			followUpDeadline = undefined;
			refreshIfVisible(refreshNow, document.hidden);
		};

		activityRef.current = () => {
			// Keep the first deadline so continuous typing cannot postpone every refresh.
			if (!document.hidden && activityTimer === undefined) {
				activityTimer = setTimeout(refreshAfterActivity, ACTIVITY_REFRESH_DELAY_MS);
			}
			// Agent attribution can arrive later than NCS edits. Recheck once after activity stops.
			followUpDeadline = Date.now() + ACTIVITY_FOLLOW_UP_DELAY_MS;
			if (followUpTimer !== undefined) {
				clearTimeout(followUpTimer);
				followUpTimer = undefined;
			}
			if (!document.hidden) {
				followUpTimer = setTimeout(refreshAfterActivitySettles, ACTIVITY_FOLLOW_UP_DELAY_MS);
			}
		};

		const onVisibilityChange = () => {
			clearTimers();
			if (!document.hidden && followUpDeadline !== undefined) {
				const remaining = followUpDeadline - Date.now();
				if (remaining > 0) {
					followUpTimer = setTimeout(refreshAfterActivitySettles, remaining);
				} else {
					// The shared refresh coordinator already fetches on tab return.
					followUpDeadline = undefined;
				}
			}
		};
		document.addEventListener('visibilitychange', onVisibilityChange);

		return () => {
			activityRef.current = undefined;
			clearTimers();
			document.removeEventListener('visibilitychange', onVisibilityChange);
		};
	}, [contentId, enabled, refreshNow]);

	return onActivity;
};
